/**
 * web/src/lib/api.ts
 * Typed fetch wrapper. Honors the FastAPI app's standard error shape
 * (src/pixel/main.py `_app_error_handler`): { error, code, details? }.
 * - Sends `credentials: 'include'` so the session cookie (D-A04 sameSite='lax') travels with every request.
 * - Sets Content-Type: application/json by default; can be overridden via init.headers.
 * - Throws ApiClientError on any non-2xx; otherwise returns parsed JSON or `undefined` for 204.
 */
import { toast } from 'sonner';

/** Mirror of the server's standard error response shape (src/pixel/main.py). */
export interface ApiErrorBody {
  error: string;
  code: string;
  details?: unknown;
}

/**
 * Normalizes any non-2xx JSON body into the canonical { error, code, details }
 * shape. The FastAPI app emits that shape from its AppError handler, but raw
 * `HTTPException(detail=...)` routes and native 422 validation errors arrive
 * wrapped under FastAPI's `detail` key instead — without this, those paths
 * surfaced `code`/`message` as `undefined` (toasts read "undefined", the
 * VALIDATION_ERROR branch misfired). F15.
 *
 * @param raw parsed JSON body (or null when the body wasn't JSON)
 * @param statusText fetch Response.statusText, used as a last-resort message
 */
export function normalizeErrorBody(raw: unknown, statusText: string): ApiErrorBody {
  const fallback: ApiErrorBody = { error: statusText || 'Request failed', code: 'UNKNOWN_ERROR' };
  if (!raw || typeof raw !== 'object') return fallback;
  const body = raw as Record<string, unknown>;

  // Canonical AppError shape: top-level error + code.
  if (typeof body.code === 'string' && typeof body.error === 'string') {
    return { error: body.error, code: body.code, details: body.details };
  }

  const detail = body.detail;
  // FastAPI RequestValidationError → detail: [{ loc, msg, type }, ...]
  if (Array.isArray(detail)) {
    const first = detail[0] as { msg?: unknown } | undefined;
    const msg = typeof first?.msg === 'string' ? first.msg : 'Validation error';
    return { error: msg, code: 'VALIDATION_ERROR', details: detail };
  }
  // Raw HTTPException(detail={ code?, error?/message? })
  if (detail && typeof detail === 'object') {
    const d = detail as Record<string, unknown>;
    const error =
      (typeof d.error === 'string' && d.error) ||
      (typeof d.message === 'string' && d.message) ||
      statusText ||
      'Request failed';
    const code = typeof d.code === 'string' ? d.code : 'HTTP_ERROR';
    return { error, code, details: d };
  }
  // Raw HTTPException(detail="some string")
  if (typeof detail === 'string') {
    return { error: detail, code: 'HTTP_ERROR' };
  }
  return fallback;
}

/**
 * Thrown by `api()` for any non-2xx response. Carries the parsed
 * `{ error, code, details? }` body verbatim so callers can branch on
 * `code` and consume `details.fieldErrors` for VALIDATION_ERROR.
 */
export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly details: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/**
 * Issues a fetch against the same-origin Fastify API and unwraps the
 * standard error shape into an ApiClientError on non-2xx.
 * @param path absolute path starting with `/` (e.g., `/api/workflows`)
 * @param init optional RequestInit; Content-Type defaults to application/json
 * @returns parsed JSON body typed as T (or undefined for 204 No Content)
 * @throws ApiClientError on any non-2xx response
 * @sideEffects performs an HTTP request with credentials: 'include'
 * @idempotent depends on the underlying route (GET = yes; POST/PUT = no)
 */
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const raw = await res.json().catch(() => null);
    const body = normalizeErrorBody(raw, res.statusText);
    throw new ApiClientError(res.status, body.code, body.details, body.error);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Shows an error toast for a failed mutation. Uses the server's message when
 * the rejection is an ApiClientError, otherwise the caller's fallback.
 * Centralizes the `err instanceof ApiClientError ? err.message : fallback`
 * ternary that was duplicated across ~16 mutation hooks.
 *
 * @param err the rejection value from a mutation (unknown by contract)
 * @param fallback message to show when `err` isn't an ApiClientError
 * @sideEffects renders a sonner error toast
 */
export function toastApiError(err: unknown, fallback: string): void {
  toast.error(err instanceof ApiClientError ? err.message : fallback);
}
